import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { MoneyTransferForm } from 'src/Components/Forms/MoneyTransfer/MoneyTransferForm.component';
import { useCreateMoneyTransfer } from 'src/Hooks/Mutations/useCreateMoneyTransfer';
import { trackLinkedReimbursementCreated } from 'src/Services/Mixpanel/events';
import { ApiError } from 'src/types/api-error.types';

/**
 * Reasons the API can refuse a reimbursement linked to expenses. Their messages name the
 * expense or the exact amount expected, which no generic wording can replace — so the
 * server text is shown as is and the catalog only covers the case where it is missing.
 */
const REIMBURSEMENT_ERROR_CODES = [
  'REIMBURSEMENT_AMOUNT_MISMATCH',
  'REIMBURSEMENT_MIXED_CASE',
  'REIMBURSEMENT_BY_PARTICIPANT_UNSUPPORTED',
  'REIMBURSEMENT_EXPENSE_NOT_ELIGIBLE',
];

const CreateMoneyTransferDrawer = () => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutate, isLoading } = useCreateMoneyTransfer();

  const getErrorMessage = (error: unknown): string => {
    const data = (error as AxiosError<ApiError>)?.response?.data;
    if (data?.code && REIMBURSEMENT_ERROR_CODES.includes(data.code)) {
      return data.message ?? t(`apiErrors.${data.code}`);
    }
    return t('common.genericError', 'Une erreur est survenue. Merci de réessayer.');
  };

  return (
    <AnimatedRoutedDrawer title={t('moneyTransfer.create.title', 'Enregistrer un remboursement')}>
      {({ onClose }) => (
        <MoneyTransferForm
          onSubmit={values => {
            setSubmitError(null);
            mutate(values, {
              onSuccess: () => {
                if (values.expenseIds?.length) {
                  trackLinkedReimbursementCreated({
                    expenses_count: values.expenseIds.length,
                    total_amount: values.amount,
                  });
                }
                onClose();
              },
              onError: error => setSubmitError(getErrorMessage(error)),
            });
          }}
          onCancel={onClose}
          isLoading={isLoading}
          submitError={submitError}
        />
      )}
    </AnimatedRoutedDrawer>
  );
};

export default CreateMoneyTransferDrawer;
